/**
 * @fileoverview Wrapper class for the Filestack JavaScript SDK.
 * This class provides a consistent interface for opening the file picker,
 * centralizes configuration, and handles passing necessary DOM context
 * to the application's custom callback functions.
 */

import * as filestack from 'filestack-js';

// Import the custom callback functions that handle picker events.
import { uploadComplete, fileSelected } from './picker-actions.js';

/**
 * Custom wrapper constructor for the Filestack picker.
 * It encapsulates the configuration parameters and manages the DOM context
 * needed by the 'picker-actions.js' callbacks.
 *
 * @param {Object} params Configuration parameters passed from InitFileStackUploader.
 * @param {string} params.apiKey Your Filestack API key.
 * @param {string} params.workflowID The ID of the Filestack workflow to execute after upload.
 * @param {Object} params.options Additional custom options for the Filestack picker.
 * @exports {Function} FileStackPicker
 */
export function FileStackPicker(params){
    this.params = params;
    this.context = {};

    this.getApiKey = function(){
        return params.apiKey;
    };

    this.getOptions = function(){
        return params.options;
    };

    this.getWorkflowID = function(){
        return params.workflowID;
    };

    this.getMaxSize = function(){
        var gigabyte = 1000 * 1024 * 1024;

        return 2 * gigabyte;
    };

    this.setContext = function (contextObject) {
        this.context = contextObject;
    };

    /**
     * Merges custom options with standard/default Filestack configuration.
     * Importantly, it wraps the callback functions to inject the DOM context.
     * @returns {Object} The complete set of options for the Filestack client.
     */
    this.getPickerOptions = function(){
        var options         = this.getOptions();
        var self = this; // Capture 'this' (the wrapper instance) for use in the callback closures.
        const triggerEl = self.context?.uploadButton?.get(0) || null;
        const maxFiles = triggerEl && triggerEl.hasAttribute('data-max-files') ? triggerEl.getAttribute('data-max-files') : 10;

        var defaultOptions = {
            // Callback for when all files are done uploading.
            // We wrap the imported function to pass the Filestack response AND our custom DOM context.
            onUploadDone: function(response) {
                uploadComplete(response, self.context)
            },
            // Callback for when a single file is selected (before upload starts).
            onFileSelected: function (file) {
                fileSelected(file, self.context);
            },
            transformations: {
                crop: false,
                circle: false,
                rotate: false
            },
            storeTo: {
                location: 'gcs',
                path: '/order-system/',
                workflows: [this.getWorkflowID()]
            },
            fromSources: ['local_file_system', 'dropbox', 'googledrive', 'onedrive'],
            maxSize: this.getMaxSize(),
            maxFiles: Number(maxFiles)
        };

        // Merge custom options with the defaults
        jQuery.extend(true, options, defaultOptions);

        return options;
    };
}

FileStackPicker.prototype.openPicker = function(){
    var client = filestack.init(this.getApiKey());

    client.picker(this.getPickerOptions()).open();

};